import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
import useFieldClass from "../../../functions/useFieldClass";
// import { useFetchJobDetails } from "../../../functions/useFetchJobDetails"; // Reusable API hook
import { useFormState } from "../../../functions/useFormState"; // Reusable form hook
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import { selectFertigation, selectFieldDescriptions } from "../../../redux/selectors";
import { selectApiStatus } from "../../../redux/slices/apiStatusSlice"; // Import the selector
import SaveBanner from "./SaveBanner";
import NoRecordFoundAlert from "../../common/NoRecordFoundAlert";
import InfoBubbleModal from "../../common/InfoBubbleModal";

const Fertigation = (zone) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails); // User details
  const userRoles = useSelector(selectUserRoles); // User details
  const currentUser = userDetails?.name;
  const sectionName = "fertigation"; // Name of the component

  const apiUrls = useSelector(selectApiUrls); // Fetch API URLs from Redux
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const { options } = useSelector((state) => state.dropdown); // Fetch dropdown values from redux

  const storedJobDetails = useSelector(selectFertigation);

  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );
  const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
  const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  const error = null;

  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails[0] || {};
  }, [storedJobDetails, activetab]);

  const { formData, handleChange, setFormData } = useFormState({});

  useEffect(() => {
    if (jobDetails) {
      setFormData({
        ID: jobDetails.ID || "",
        watertronicsFertigation: jobDetails.WatertronicsFertigation || "",
        watertronicsStyle: jobDetails.WatertronicsStyle || "",
        tankSize: jobDetails.TankSize || "",
        tankBottomType: jobDetails.TankBottomType || "",
        liquidTankByWatertronics: jobDetails.LiquidTankByWatertronics || "",
        tankQuantity: jobDetails.TankQuantity || "",
        tankLevelSensor: jobDetails.TankLevelSensor || "",
        tankLevelSensorQuantity: jobDetails.TankLevelSensorQuantity || "",
        fertigationComments: jobDetails.FertigationComments || "",
        zone: jobDetails.Zone || "",
        hasTBD: jobDetails.HasTBD || "",
        hasFertigation: jobDetails.HasFertigation || "",
        fertigationQuantity: jobDetails.FertigationQuantity || "",
        quantity10: jobDetails.Quantity10 || "",
        quantity25: jobDetails.Quantity25 || "",
        hoseType: jobDetails.HoseType || "",
        quillType: jobDetails.QuillType || "",
      });
    }
  }, [dispatch, jobDetails, setFormData]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  // State to store role variables
  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });

  const [showInfoBubbleModal, setShowInfoBubbleModal] = useState(false);

  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData);

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  const fieldClasses = useFieldClass(formData, zone); // Use useFieldClass hook

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ); // Show loading spinner
  }

  if (error) {
    return <div className="error">{error}</div>; // Show error message if fetch fails
  }

  if (!loading && !jobDetails?.ID) {
    return (
      <NoRecordFoundAlert sectionName={sectionName} zone={zone} />
    );
  }

  /****************Add dropdown options here*************************/
  
  const watertronicsFertigationOptions =
    options?.filter(
      (option) => option.DropdownType === "Watertronics Fertigation"
    ).sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const genericOptions =
    options?.filter((option) => option.DropdownType === "Generic Yes/ No")
    .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const tankBottomTypeOptions =
    options?.filter((option) => option.DropdownType === "Tank Bottom Type")
    .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const hoseTypeOptions =
    options?.filter((option) => option.DropdownType === "Hose Type")
    .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const quillTypeOptions =
    options?.filter((option) => option.DropdownType === "Quill Type")
    .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const quantity10Options =
    options?.filter((option) => option.DropdownType === "Quantity 10")
    .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const quantity25Options =
    options?.filter((option) => option.DropdownType === "Quantity 25")
    .sort((a, b) => a.SortOrder - b.SortOrder) || [];

  /***************************************************************** */

  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  return (
    <div>
      {loading ? (
        <div className="d-flex justify-content-center align-items-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <div className="mechanical-responsive-grid fertigation-responsive">
          <SaveBanner showNotification={showNotification} saveStatus={saveStatus} />
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="watertronicsFertigation">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("WatertronicsFertigation")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Watertronics Fertigation</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="watertronicsFertigation"
                    value={formData.watertronicsFertigation || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(
                        e,
                        "Watertronics Fertigation",
                        watertronicsFertigationOptions,
                        {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "WatertronicsFertigation",
                          tableKey,
                        }
                      )
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.watertronicsFertigation}
                    onChange={handleChange}
                  >
                    <option value="">Select Watertronics Fertigation</option>
                    {watertronicsFertigationOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(
                      formData.watertronicsFertigation,
                      watertronicsFertigationOptions
                    ) &&
                      formData.watertronicsFertigation && (
                        <option
                          value={formData.watertronicsFertigation}
                          style={{ color: "red" }}
                        >
                          *{formData.watertronicsFertigation}
                        </option>
                      )}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="watertronicsStyle">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("WatertronicsStyle")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Watertronics Style</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="watertronicsStyle"
                    value={formData.watertronicsStyle || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Watertronics Style", genericOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "WatertronicsStyle",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.watertronicsStyle}
                    onChange={handleChange}
                  >
                    <option value="">Select Watertronics Style</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(
                      formData.watertronicsStyle,
                      genericOptions
                    ) &&
                      formData.watertronicsStyle && (
                        <option
                          value={formData.watertronicsStyle}
                          style={{ color: "red" }}
                        >
                          *{formData.watertronicsStyle}
                        </option>
                      )}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="tankSize">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("TankSize")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Tank Size</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Control
                    type="text"
                    name="tankSize"
                    value={formData.tankSize || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Tank Size", [], {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "TankSize",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.tankSize}
                    onChange={handleChange}
                    placeholder="Enter Tank Size"
                  />
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="tankBottomType">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("TankBottomType")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Tank Bottom Type</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="tankBottomType"
                    value={formData.tankBottomType || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Tank Bottom Type", tankBottomTypeOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "TankBottomType",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.tankBottomType}
                    onChange={handleChange}
                  >
                    <option value="">Select Tank Bottom Type</option>
                    {tankBottomTypeOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(
                      formData.tankBottomType,
                      tankBottomTypeOptions
                    ) &&
                      formData.tankBottomType && (
                        <option
                          value={formData.tankBottomType}
                          style={{ color: "red" }}
                        >
                          *{formData.tankBottomType}
                        </option>
                      )}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="liquidTankByWatertronics">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("LiquidTankByWatertronics")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Liquid Tank By Watertronics</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="liquidTankByWatertronics"
                    value={formData.liquidTankByWatertronics || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Liquid Tank By Watertronics", genericOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "LiquidTankByWatertronics",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.liquidTankByWatertronics}
                    onChange={handleChange}
                  >
                    <option value="">Select Liquid Tank By Watertronics</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(
                      formData.liquidTankByWatertronics,
                      genericOptions
                    ) &&
                      formData.liquidTankByWatertronics && (
                        <option
                          value={formData.liquidTankByWatertronics}
                          style={{ color: "red" }}
                        >
                          *{formData.liquidTankByWatertronics}
                        </option>
                      )}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="tankQuantity">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("TankQuantity")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Tank Quantity</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Control
                    type="text"
                    name="tankQuantity"
                    value={formData.tankQuantity || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Tank Quantity", [], {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "TankQuantity",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.tankQuantity}
                    onChange={handleChange}
                    placeholder="Enter Tank Quantity"
                  />
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="tankLevelSensor">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("TankLevelSensor")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Tank Level Sensor</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="tankLevelSensor"
                    value={formData.tankLevelSensor || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Tank Level Sensor", genericOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "TankLevelSensor",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.tankLevelSensor}
                    onChange={handleChange}
                  >
                    <option value="">Select Tank Level Sensor</option>
                    {genericOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(
                      formData.tankLevelSensor,
                      genericOptions
                    ) &&
                      formData.tankLevelSensor && (
                        <option
                          value={formData.tankLevelSensor}
                          style={{ color: "red" }}
                        >
                          *{formData.tankLevelSensor}
                        </option>
                      )}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="tankLevelSensorQuantity">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("TankLevelSensorQuantity")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Tank Level Sensor Quantity</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Control
                    type="text"
                    name="tankLevelSensorQuantity"
                    value={formData.tankLevelSensorQuantity || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Tank Level Sensor Quantity", [], {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "TankLevelSensorQuantity",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.tankLevelSensorQuantity}
                    onChange={handleChange}
                    placeholder="Enter Tank Level Sensor Quantity"
                  />
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="fertigationQuantity">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("FertigationQuantity")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Fertigation Quantity</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Control
                    type="text"
                    name="fertigationQuantity"
                    value={formData.fertigationQuantity || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Fertigation Quantity", [], {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "FertigationQuantity",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.fertigationQuantity}
                    onChange={handleChange}
                    placeholder="Enter Fertigation Quantity"
                  />
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="quantity10">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Quantity10")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Quantity of 10' Hose</span>
                  </OverlayTrigger>
                  <i 
                    className="bi bi-info-circle ms-2 text-primary" 
                    style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                    onClick={() => setShowInfoBubbleModal(true)}
                    title="Click for information"
                  ></i>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="quantity10"
                    value={formData.quantity10 || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Quantity 10", quantity10Options, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "Quantity10",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.quantity10}
                    onChange={handleChange}
                  >
                    <option value="">Select Quantity 10</option>
                    {quantity10Options.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(
                      formData.quantity10,
                      quantity10Options
                    ) &&
                      formData.quantity10 && (
                        <option
                          value={formData.quantity10}
                          style={{ color: "red" }}
                        >
                          *{formData.quantity10}
                        </option>
                      )}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="quantity25">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("Quantity25")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Quantity of 25' Hose</span>
                  </OverlayTrigger>
                  <i 
                    className="bi bi-info-circle ms-2 text-primary" 
                    style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                    onClick={() => setShowInfoBubbleModal(true)}
                    title="Click for information"
                  ></i>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="quantity25"
                    value={formData.quantity25 || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Quantity 25", quantity25Options, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "Quantity25",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.quantity25}
                    onChange={handleChange}
                  >
                    <option value="">Select Quantity 25</option>
                    {quantity25Options.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(
                      formData.quantity25,
                      quantity25Options
                    ) &&
                      formData.quantity25 && (
                        <option
                          value={formData.quantity25}
                          style={{ color: "red" }}
                        >
                          *{formData.quantity25}
                        </option>
                      )}
                  </Form.Select>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group as={Row} controlId="hoseType">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("HoseType")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Hose Type</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="hoseType"
                    value={formData.hoseType || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Hose Type", hoseTypeOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "HoseType",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.hoseType}
                    onChange={handleChange}
                  >
                    <option value="">Select Hose Type</option>
                    {hoseTypeOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(
                      formData.hoseType,
                      hoseTypeOptions
                    ) &&
                      formData.hoseType && (
                        <option
                          value={formData.hoseType}
                          style={{ color: "red" }}
                        >
                          *{formData.hoseType}
                        </option>
                      )}
                  </Form.Select>
                  <small className="text-muted d-block mt-1">Polypropylene only used with Chlorine applications</small>
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group as={Row} controlId="quillType">
                <Form.Label column md={3}>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("QuillType")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Quill Type</span>
                  </OverlayTrigger>
                </Form.Label>
                <Col>
                  <Form.Select
                    name="quillType"
                    value={formData.quillType || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Quill Type", quillTypeOptions, {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "QuillType",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.quillType}
                    onChange={handleChange}
                  >
                    <option value="">Select Quill Type</option>
                    {quillTypeOptions.map((option) => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                    {!isValueInDropdown(
                      formData.quillType,
                      quillTypeOptions
                    ) &&
                      formData.quillType && (
                        <option
                          value={formData.quillType}
                          style={{ color: "red" }}
                        >
                          *{formData.quillType}
                        </option>
                      )}
                  </Form.Select>
                  <small className="text-muted d-block mt-1">PVDF only used with Chlorine applications</small>
                </Col>
              </Form.Group>
            </Col>

            <Col>
              {/* Empty column for alignment */}
            </Col>
          </Row>
          <Row className="mb-3 mechanical-form-full-width">
            <Col>
              <Form.Group controlId="fertigationComments">
                <Form.Label>
                  <OverlayTrigger
                    placement="top"
                    overlay={renderTooltip("FertigationComments")}
                    delay={{ show: 300, hide: 200 }}
                  >
                    <span>Comments</span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="fertigationComments"
                  value={formData.fertigationComments || ""}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(e, "Fertigation Comments", [], {
                      ID: formData.ID,
                      url: apiUrl,
                      field: "FertigationComments",
                      tableKey,
                    })
                  }
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={fieldClasses.fertigationComments}
                  onChange={handleChange}
                  placeholder="Enter Comments"
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
      )}

      {/* Info Bubble Modal */}
      <InfoBubbleModal
        show={showInfoBubbleModal}
        onHide={() => setShowInfoBubbleModal(false)}
        sectionName="Fertigation"
        value="Hose Quantity"
        title="Hose Quantity Information"
        displayAsOrderedList={false}
      />
    </div>
  );
};

export default Fertigation;
